import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, CircularProgress,
  Snackbar, Alert, Grid, Tooltip, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, TablePagination, LinearProgress, useTheme,
  alpha, Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon, Payment as PaymentIcon,
  Business as BusinessIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon, AttachMoney as MoneyIcon, Refresh as RefreshIcon,
  Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import { v4 as uuidv4 } from 'uuid';

interface VendorBill {
  id: string;
  userId: string;
  vendorId: string;
  vendorName: string;
  billNumber: string;
  date: string;
  dueDate: string;
  amount: number;
  status: 'draft' | 'unpaid' | 'paid';
  paymentDate?: string;
  paymentMode?: 'cash' | 'bank';
  reference?: string;
  notes?: string;
  createdAt: number;
  updatedAt: string;
}

interface Vendor { id: string; name: string; }

const getAgingBucket = (dueDate: string): string => {
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(dueDate); due.setHours(0,0,0,0);
  const d = Math.floor((today.getTime() - due.getTime()) / 86400000);
  if (d <= 0) return 'Current';
  if (d <= 30) return '1-30 Days';
  if (d <= 60) return '31-60 Days';
  if (d <= 90) return '61-90 Days';
  return '90+ Days';
};
const getDaysOverdue = (dueDate: string): number => {
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(dueDate); due.setHours(0,0,0,0);
  return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86400000));
};
const bucketColor: Record<string, string> = {
  'Current': '#059669', '1-30 Days': '#D97706',
  '31-60 Days': '#DC2626', '61-90 Days': '#7C3AED', '90+ Days': '#1F2937',
};

const SummaryCard: React.FC<{title:string;value:string;subtitle?:string;icon:React.ReactNode;color:string}> = ({title,value,subtitle,icon,color}) => (
  <Paper elevation={0} sx={{
    p:3, borderRadius:4, background:`${color}10`, border:`1px solid ${color}22`,
    height:'100%', display:'flex', flexDirection:'column', justifyContent:'space-between',
    transition:'all 0.3s ease', '&:hover':{boxShadow:`0 8px 25px ${color}18`, transform:'translateY(-4px)'},
  }}>
    <Box display="flex" alignItems="center" gap={2} mb={1.5}>
      <Box sx={{width:44,height:44,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:`${color}22`,color}}>{icon}</Box>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>{title}</Typography>
    </Box>
    <Typography variant="h4" fontWeight="bold" sx={{color}}>{value}</Typography>
    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
  </Paper>
);

const EMPTY_BILL = (): Partial<VendorBill> => ({
  vendorId:'', vendorName:'', billNumber:'',
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date(Date.now()+15*86400000).toISOString().split('T')[0],
  amount:0, status:'unpaid', reference:'', notes:'',
});

const AccountsPayable: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [bills, setBills] = useState<VendorBill[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({open:false, message:'', severity:'success' as 'success'|'error'});

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBill, setEditBill] = useState<Partial<VendorBill>>(EMPTY_BILL());
  const [saving, setSaving] = useState(false);

  // Pay dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payBill, setPayBill] = useState<VendorBill | null>(null);
  const [payMode, setPayMode] = useState<'cash'|'bank'>('cash');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchAll = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const allBills = await indexedDBService.getAllItems<VendorBill>('vendorBills');
      setBills(allBills.filter(b => b.userId === currentUser.uid).sort((a,b) => b.createdAt - a.createdAt));
      const allVendors = await indexedDBService.getAllItems<any>('vendors');
      setVendors(allVendors.filter((v:any) => v.userId === currentUser.uid).map((v:any) => ({id:v.id, name:v.name||v.displayName||'Vendor'})));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentUser]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveBill = async () => {
    if (!currentUser || !editBill.vendorName || !editBill.dueDate || !editBill.amount) {
      setSnackbar({open:true, message:'Please fill Vendor, Amount, and Due Date', severity:'error'}); return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const bill: VendorBill = {
        id: editBill.id || uuidv4(),
        userId: currentUser.uid,
        vendorId: editBill.vendorId||'',
        vendorName: editBill.vendorName!,
        billNumber: editBill.billNumber || `BILL-${Date.now().toString().slice(-6)}`,
        date: editBill.date!,
        dueDate: editBill.dueDate!,
        amount: Number(editBill.amount)||0,
        status: editBill.status as VendorBill['status'] || 'unpaid',
        reference: editBill.reference||'',
        notes: editBill.notes||'',
        createdAt: editBill.createdAt || Date.now(),
        updatedAt: now,
      };
      await indexedDBService.putItem('vendorBills', bill);
      setSnackbar({open:true, message: editBill.id ? 'Bill updated' : 'Bill added', severity:'success'});
      setDialogOpen(false);
      fetchAll();
    } catch { setSnackbar({open:true, message:'Failed to save bill', severity:'error'}); }
    finally { setSaving(false); }
  };

  const markPaid = async () => {
    if (!payBill || !currentUser) return;
    try {
      await indexedDBService.putItem('vendorBills', {
        ...payBill, status:'paid', paymentDate:payDate, paymentMode:payMode, updatedAt:new Date().toISOString(),
      });
      setSnackbar({open:true, message:`Bill #${payBill.billNumber} marked as paid`, severity:'success'});
      setPayOpen(false); fetchAll();
    } catch { setSnackbar({open:true, message:'Failed to update bill', severity:'error'}); }
  };

  const deleteBill = async (bill: VendorBill) => {
    try {
      await indexedDBService.deleteItem('vendorBills', bill.id);
      setSnackbar({open:true, message:'Bill deleted', severity:'success'}); fetchAll();
    } catch { setSnackbar({open:true, message:'Failed to delete', severity:'error'}); }
  };

  const openAdd = () => { setEditBill(EMPTY_BILL()); setDialogOpen(true); };
  const openEdit = (b: VendorBill) => { setEditBill({...b}); setDialogOpen(true); };

  // Stats
  const unpaidBills = bills.filter(b => b.status !== 'paid');
  const totalPayable = unpaidBills.reduce((s,b)=>s+b.amount, 0);
  const overdueBills = unpaidBills.filter(b => getDaysOverdue(b.dueDate) > 0);
  const totalOverdue = overdueBills.reduce((s,b)=>s+b.amount, 0);
  const dueThisWeek = unpaidBills.filter(b => {
    const days = getDaysOverdue(b.dueDate); return days <= 0 && days > -8;
  }).reduce((s,b)=>s+b.amount,0);

  const BUCKET_ORDER = ['Current','1-30 Days','31-60 Days','61-90 Days','90+ Days'];
  const agingMap: Record<string,{total:number;count:number}> = {};
  BUCKET_ORDER.forEach(l => { agingMap[l] = {total:0, count:0}; });
  unpaidBills.forEach(b => { const bk = getAgingBucket(b.dueDate); agingMap[bk].total+=b.amount; agingMap[bk].count++; });

  const filtered = bills.filter(b => {
    const ms = !search || b.vendorName.toLowerCase().includes(search.toLowerCase()) || b.billNumber.toLowerCase().includes(search.toLowerCase());
    const mst = statusFilter === 'All' || b.status === statusFilter;
    return ms && mst;
  });

  return (
    <Box sx={{p:{xs:1,sm:2}}}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{background:'linear-gradient(45deg, #DC2626 30%, #F97316 90%)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
            Accounts Payable
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>Track vendor bills and outstanding payments</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAll} disabled={loading} sx={{borderRadius:2}}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{borderRadius:2, background:'linear-gradient(45deg,#DC2626,#F97316)'}}>Add Bill</Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{mb:2,borderRadius:2}} />}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Total Payable" value={`₹${totalPayable.toLocaleString('en-IN',{minimumFractionDigits:2})}`} subtitle={`${unpaidBills.length} unpaid bills`} icon={<MoneyIcon />} color="#DC2626" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Overdue Bills" value={`₹${totalOverdue.toLocaleString('en-IN',{minimumFractionDigits:2})}`} subtitle={`${overdueBills.length} overdue`} icon={<WarningIcon />} color="#D97706" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Due This Week" value={`₹${dueThisWeek.toLocaleString('en-IN',{minimumFractionDigits:2})}`} subtitle="upcoming" icon={<AccessTimeIcon />} color="#7C3AED" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Total Vendors" value={String(new Set(unpaidBills.map(b=>b.vendorName)).size)} subtitle="with outstanding dues" icon={<BusinessIcon />} color="#059669" /></Grid>
      </Grid>

      {/* Aging buckets */}
      <Typography variant="h6" fontWeight="bold" mb={1.5}>Payable Aging</Typography>
      <Grid container spacing={2} mb={3}>
        {BUCKET_ORDER.map(bucket => {
          const data = agingMap[bucket];
          const color = bucketColor[bucket];
          const pct = totalPayable > 0 ? (data.total / totalPayable) * 100 : 0;
          return (
            <Grid item xs={12} sm={6} md key={bucket}>
              <Paper elevation={0} sx={{p:2,borderRadius:3,border:`2px solid ${color}33`,background:`${color}10`,transition:'all 0.2s',cursor:'default'}}>
                <Typography variant="caption" fontWeight={700} sx={{color,textTransform:'uppercase',letterSpacing:0.5}}>{bucket}</Typography>
                <Typography variant="h6" fontWeight="bold" mt={0.5}>₹{data.total.toLocaleString('en-IN',{minimumFractionDigits:2})}</Typography>
                <Typography variant="caption" color="text.secondary">{data.count} bills</Typography>
                <LinearProgress variant="determinate" value={pct} sx={{mt:1,borderRadius:2,height:5,backgroundColor:`${color}22`,'& .MuiLinearProgress-bar':{backgroundColor:color,borderRadius:2}}} />
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Bills Table */}
      <Paper elevation={0} sx={{borderRadius:3,border:'1px solid',borderColor:'divider',overflow:'hidden'}}>
        <Box p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center" sx={{borderBottom:'1px solid',borderColor:'divider'}}>
          <TextField size="small" placeholder="Search vendor or bill #..." value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}}
            InputProps={{startAdornment:<InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}
            sx={{flex:1,minWidth:200,'& .MuiOutlinedInput-root':{borderRadius:2}}}
          />
          <FormControl size="small" sx={{minWidth:140}}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e=>{setStatusFilter(e.target.value);setPage(0);}} sx={{borderRadius:2}}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{background:'linear-gradient(45deg,#DC2626,#F97316)'}}>
                {['Vendor','Bill #','Date','Due Date','Amount','Status','Aging','Actions'].map(h=>(
                  <TableCell key={h} sx={{color:'white',fontWeight:'bold',whiteSpace:'nowrap'}}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page*rowsPerPage, page*rowsPerPage+rowsPerPage).map(bill => {
                const daysOv = getDaysOverdue(bill.dueDate);
                const bucket = bill.status==='paid' ? 'Current' : getAgingBucket(bill.dueDate);
                const color = bucketColor[bucket];
                return (
                  <TableRow key={bill.id} hover>
                    <TableCell sx={{fontWeight:500,whiteSpace:'nowrap'}}>
                      <Box display="flex" alignItems="center" gap={1}><BusinessIcon fontSize="small" sx={{color:'text.secondary'}}/>{bill.vendorName}</Box>
                    </TableCell>
                    <TableCell sx={{fontFamily:'monospace',fontSize:'0.82rem'}}>{bill.billNumber}</TableCell>
                    <TableCell sx={{whiteSpace:'nowrap'}}>{bill.date}</TableCell>
                    <TableCell sx={{whiteSpace:'nowrap',color:bill.status!=='paid'&&daysOv>0?'#DC2626':'text.primary',fontWeight:bill.status!=='paid'&&daysOv>0?600:400}}>{bill.dueDate}</TableCell>
                    <TableCell sx={{fontWeight:600}}>₹{bill.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={bill.status.toUpperCase()} size="small"
                        color={bill.status==='paid'?'success':bill.status==='unpaid'?'error':'default'}
                        sx={{fontWeight:700,fontSize:'0.73rem'}}
                      />
                    </TableCell>
                    <TableCell>
                      {bill.status==='paid'
                        ? <Chip label="Paid" size="small" color="success" variant="outlined"/>
                        : <Chip label={bucket} size="small" sx={{bgcolor:`${color}18`,color,border:`1px solid ${color}44`,fontWeight:600,fontSize:'0.73rem'}}/>
                      }
                    </TableCell>
                    <TableCell sx={{whiteSpace:'nowrap'}}>
                      {bill.status!=='paid' && (
                        <Tooltip title="Mark as Paid">
                          <IconButton size="small" color="success" onClick={()=>{setPayBill(bill);setPayDate(new Date().toISOString().split('T')[0]);setPayMode('cash');setPayOpen(true);}}>
                            <PaymentIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={()=>openEdit(bill)}><EditIcon fontSize="small"/></IconButton>
                      </Tooltip>
                      {bill.status!=='paid' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={()=>deleteBill(bill)}><DeleteIcon fontSize="small"/></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length===0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{py:5}}>
                  <CheckCircleIcon sx={{fontSize:40,color:'success.main',mb:1}}/>
                  <Typography variant="subtitle1" color="text.secondary">{bills.length===0?'No vendor bills yet. Click "Add Bill" to start.':'No bills match your filters'}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10,25,50]} component="div" count={filtered.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_,p)=>setPage(p)}
          onRowsPerPageChange={e=>{setRowsPerPage(parseInt(e.target.value,10));setPage(0);}}
        />
      </Paper>

      {/* Add/Edit Bill Dialog */}
      <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{fontWeight:'bold'}}>{editBill.id ? 'Edit Vendor Bill' : 'Add Vendor Bill'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
            <TextField label="Vendor Name *" value={editBill.vendorName||''} onChange={e=>setEditBill(p=>({...p,vendorName:e.target.value}))} fullWidth size="small"/>
            <TextField label="Bill Number" value={editBill.billNumber||''} onChange={e=>setEditBill(p=>({...p,billNumber:e.target.value}))} fullWidth size="small"/>
            <Box display="flex" gap={2}>
              <TextField label="Bill Date" type="date" value={editBill.date||''} onChange={e=>setEditBill(p=>({...p,date:e.target.value}))} fullWidth size="small" InputLabelProps={{shrink:true}}/>
              <TextField label="Due Date *" type="date" value={editBill.dueDate||''} onChange={e=>setEditBill(p=>({...p,dueDate:e.target.value}))} fullWidth size="small" InputLabelProps={{shrink:true}}/>
            </Box>
            <TextField label="Amount *" type="number" value={editBill.amount||''} onChange={e=>setEditBill(p=>({...p,amount:Number(e.target.value)}))} fullWidth size="small"
              InputProps={{startAdornment:<InputAdornment position="start">₹</InputAdornment>}}/>
            <TextField label="Reference / Bill No." value={editBill.reference||''} onChange={e=>setEditBill(p=>({...p,reference:e.target.value}))} fullWidth size="small"/>
            <TextField label="Notes" value={editBill.notes||''} onChange={e=>setEditBill(p=>({...p,notes:e.target.value}))} fullWidth size="small" multiline rows={2}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{p:2,gap:1}}>
          <Button onClick={()=>setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBill} disabled={saving}
            startIcon={saving?<CircularProgress size={16} color="inherit"/>:undefined}
            sx={{borderRadius:2,background:'linear-gradient(45deg,#DC2626,#F97316)'}}>
            {editBill.id ? 'Update' : 'Add Bill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={payOpen} onClose={()=>setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontWeight:'bold', display:'flex', alignItems:'center', gap:1}}>
          <PaymentIcon color="success"/> Record Payment
        </DialogTitle>
        <DialogContent>
          {payBill && (
            <Box mb={2} p={1.5} borderRadius={2} sx={{bgcolor:alpha(theme.palette.success.main,0.06),border:`1px solid ${alpha(theme.palette.success.main,0.2)}`}}>
              <Typography variant="subtitle2">{payBill.vendorName} — #{payBill.billNumber}</Typography>
              <Typography variant="body2">Amount: <strong>₹{payBill.amount.toFixed(2)}</strong></Typography>
            </Box>
          )}
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Payment Mode</InputLabel>
            <Select value={payMode} label="Payment Mode" onChange={e=>setPayMode(e.target.value as any)}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank / UPI / Cheque</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" label="Payment Date" type="date" value={payDate} onChange={e=>setPayDate(e.target.value)} InputLabelProps={{shrink:true}} size="small"/>
        </DialogContent>
        <DialogActions sx={{p:2,gap:1}}>
          <Button onClick={()=>setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={markPaid} sx={{borderRadius:2}}>Mark as Paid</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={()=>setSnackbar(s=>({...s,open:false}))} anchorOrigin={{vertical:'bottom',horizontal:'center'}}>
        <Alert severity={snackbar.severity} onClose={()=>setSnackbar(s=>({...s,open:false}))} sx={{borderRadius:2}}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountsPayable;
